import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  Activity,
  FlaskConical,
  ShieldPlus,
  Brain,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mail,
  Lock,
  Smartphone,
  ShieldCheck,
  Users,
  UserCheck,
  ArrowLeft,
} from 'lucide-react';
import { useAuth, AccountDeactivatedError } from '../../context/AuthContext';
import './Login.css';

type LoginMethod = 'password' | 'otp';
type SelectedRole = 'employee' | 'corporate_hr' | 'super_admin' | null;

// Only show these roles in the role selection cards
const visibleRoles: Array<'employee' | 'corporate_hr'> = ['employee', 'corporate_hr'];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['auth', 'common']);
  const { login, loginWithOtp, verifyMFA, requestOtp, isAuthenticated, isLoading, error, clearError, mfaState, cancelMFA, user } = useAuth();

  const roleConfig = {
    employee: {
      label: t('auth:roles.employee'),
      description: t('auth:roles.employeeDesc'),
      icon: UserCheck,
      color: '#06b6d4',
      bg: '#ecfeff',
    },
    corporate_hr: {
      label: t('auth:roles.corporateHR'),
      description: t('auth:roles.corporateHRDesc'),
      icon: Users,
      color: '#8b5cf6',
      bg: '#f5f3ff',
    },
    super_admin: {
      label: t('auth:roles.superAdmin'),
      description: t('auth:roles.superAdminDesc'),
      icon: ShieldCheck,
      color: '#f43f5e',
      bg: '#fff1f2',
    },
  };

  // Role selection
  // Returning from "continue as an individual?" (answered No): reopen the
  // employee sign-in with a short note.
  const [loginNotice, setLoginNotice] = useState<string | undefined>((location.state as any)?.notice);
  useEffect(() => {
    // Show it once: drop it from history so a reload doesn't bring it back.
    if ((location.state as any)?.notice) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedRole, setSelectedRole] = useState<SelectedRole>(
    (location.state as any)?.role === 'employee' ? ('employee' as SelectedRole) : null,
  );

  // Hidden admin access — triple-click logo
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [logoClickTimer, setLogoClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [adminMode, setAdminMode] = useState(false);

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);

    // Reset counter after 1.5s of no clicks
    if (logoClickTimer) clearTimeout(logoClickTimer);
    const timer = setTimeout(() => setLogoClickCount(0), 1500);
    setLogoClickTimer(timer);

    if (newCount >= 3) {
      setAdminMode(true);
      setAuthStep('identifier');
      setSelectedRole('super_admin');
      setLogoClickCount(0);
      if (logoClickTimer) clearTimeout(logoClickTimer);
    }
  };

  // Form state
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  // Password login is a two-step wizard: collect the identifier first, then the
  // password. The identifier is NOT verified against the server on "Continue" —
  // the real check still happens when both are submitted to /auth/password-login/.
  const [authStep, setAuthStep] = useState<'identifier' | 'password'>('identifier');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  // Handle MFA verification
  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaState.userId || !mfaState.mfaToken || !mfaCode) return;
    try {
      await verifyMFA({ user_id: mfaState.userId, code: mfaCode, mfa_token: mfaState.mfaToken });
    } catch {
      // Error handled by context
    }
  };

  // Redirect if already authenticated. Send admin/HR users straight to their
  // portal home so they don't need to hop through the employee shell.
  useEffect(() => {
    if (isAuthenticated) {
      const explicitFrom = (location.state as any)?.from?.pathname;
      let target = explicitFrom || '/';
      if (!explicitFrom) {
        if (user?.role === 'super_admin') target = '/admin/corporates';
        else if (user?.role === 'corporate_admin' || user?.role === 'corporate_hr') target = '/hr';
      }
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, location, user?.role]);

  // Clear errors when switching methods
  useEffect(() => {
    clearError();
    setLocalError('');
    setOtpSent(false);
    setOtp('');
    setOtpMessage('');
    setAuthStep('identifier');
    setPassword('');
  }, [loginMethod, clearError]);

  // Handle going back to role selection
  const handleBackToRoles = () => {
    setLoginNotice(undefined);
    setSelectedRole(null);
    setAdminMode(false);
    setAuthStep('identifier');
    setEmail('');
    setUsername('');
    setPassword('');
    setIdentifier('');
    setOtp('');
    setOtpSent(false);
    setOtpMessage('');
    setLocalError('');
    clearError();
  };

  // Step 1 -> Step 2: validate the identifier client-side, then reveal the
  // password field. No API call happens here (no username-exists endpoint).
  const handleIdentifierContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (selectedRole === 'employee') {
      if (!username.trim()) {
        setLocalError(t('auth:errors.enterUsername'));
        return;
      }
    } else {
      const value = email.trim();
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setLocalError(t('auth:errors.enterEmail'));
        return;
      }
    }

    setAuthStep('password');
  };

  // Step 2 -> Step 1: let the user correct their username/email.
  const handleBackToIdentifier = () => {
    setAuthStep('identifier');
    setPassword('');
    setShowPassword(false);
    setLocalError('');
    clearError();
  };

  // Handle password login
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (selectedRole === 'employee') {
      if (!username || !password) {
        setLocalError(t('auth:errors.enterUsernamePassword'));
        return;
      }
      const variants = Array.from(
        new Set([username, username.toLowerCase(), username.toUpperCase()])
      );
      let lastErr: any = null;
      for (const variant of variants) {
        try {
          await login({ username: variant, password });
          lastErr = null;
          break;
        } catch (err: any) {
          lastErr = err;
          if (err instanceof AccountDeactivatedError) {
            navigate('/continue-as-individual', { state: { offer: err.offer } });
            return;
          }
          const status = err?.response?.status;
          // Only keep trying other casings for "user not found / invalid credentials" style failures
          if (status && status !== 400 && status !== 401 && status !== 404) {
            break;
          }
        }
      }
      if (lastErr) {
        // Error is surfaced by context
      }
    } else {
      if (!email || !password) {
        setLocalError(t('auth:errors.enterEmailPassword'));
        return;
      }
      try {
        await login({ email, password });
      } catch (err: any) {
        if (err instanceof AccountDeactivatedError) {
          navigate('/continue-as-individual', { state: { offer: err.offer } });
        }
        // Other errors are handled by context
      }
    }
  };

  // Handle OTP request
  const handleRequestOtp = async () => {
    setLocalError('');
    setOtpMessage('');

    if (!identifier) {
      setLocalError(t('auth:errors.enterEmailOrMobile'));
      return;
    }

    setOtpSending(true);
    try {
      const message = await requestOtp({ identifier });
      setOtpSent(true);
      setOtpMessage(message);
    } catch (err: any) {
      // Error is handled by context
    } finally {
      setOtpSending(false);
    }
  };

  // Handle OTP login
  const handleOtpLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!identifier || !otp) {
      setLocalError(t('auth:errors.enterIdentifierOtp'));
      return;
    }

    try {
      await loginWithOtp({ identifier, otp });
    } catch (err: any) {
      if (err instanceof AccountDeactivatedError) {
        navigate('/continue-as-individual', { state: { offer: err.offer } });
      }
      // Other errors are handled by context
    }
  };

  const displayError = localError || error;

  const features = [
    { icon: Activity, text: t('auth:features.healthRecords') },
    { icon: ShieldPlus, text: t('auth:features.insurance') },
    { icon: FlaskConical, text: t('auth:features.smartReports') },
    { icon: Heart, text: t('auth:features.womensHealth') },
    { icon: Brain, text: t('auth:features.aiAssessments') },
  ];

  const currentRole = selectedRole ? roleConfig[selectedRole] : null;

  return (
    <div className="login-page">
      {/* Decorative background shapes */}
      <div className="login-bg-shape shape-1" />
      <div className="login-bg-shape shape-2" />
      <div className="login-bg-shape shape-3" />

      <div className="login-container">
        {/* Left Side - Branding */}
        <div className="login-left">
          <div className="login-branding">
            <div className="login-logo-mark" onClick={handleLogoClick} style={{ cursor: 'default', userSelect: 'none' }}>
              <img src="/images/logo.svg" alt="Infinicare" style={{ height: 100 }} draggable={false} />
            </div>
            <p className="login-tagline">{t('auth:tagline')}</p>

            <div className="login-features">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="feature-item" style={{ animationDelay: `${0.2 + idx * 0.1}s` }}>
                    <div className="feature-icon-wrapper">
                      <Icon size={20} />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="login-left-footer">
              <p>{t('auth:trustedBy')}</p>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="login-right">
          <div className="login-form-container">

            {/* Step 1: Role Selection */}
            {!selectedRole && (
              <div className="role-selection">
                <h2 className="login-title">{t('auth:welcome')}</h2>
                <p className="login-subtitle">{t('auth:howToSignIn')}</p>

                <div className="role-cards">
                  {visibleRoles.map((key) => {
                    const role = roleConfig[key];
                    const Icon = role.icon;
                    return (
                      <button
                        key={key}
                        className="role-card"
                        onClick={() => {
                          setAuthStep('identifier');
                          setPassword('');
                          setSelectedRole(key);
                        }}
                        type="button"
                      >
                        <div className="role-card-icon" style={{ background: role.bg, color: role.color }}>
                          <Icon size={24} />
                        </div>
                        <div className="role-card-text">
                          <span className="role-card-label">{role.label}</span>
                          <span className="role-card-desc">{role.description}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="login-footer">
                  <p>
                    {t('auth:register.dontHaveAccount')}{' '}
                    <Link to="/register/corporate" className="register-link">
                      {t('auth:corporateRegister.registerWithCode')}
                    </Link>
                  </p>
                  <p>
                    {t('auth:choosePlan.newHere')}{' '}
                    <Link to="/choose-plan" className="register-link">
                      {t('auth:choosePlan.seePlans')}
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {/* MFA Verification Step */}
            {mfaState.required && mfaState.userId && mfaState.mfaToken && (
              <div className="login-credentials">
                <button type="button" className="back-to-roles" onClick={() => { cancelMFA(); setMfaCode(''); }}>
                  <ArrowLeft size={18} />
                  <span>{t('common:buttons.back')}</span>
                </button>
                <h2 className="login-title">{t('auth:mfa.title')}</h2>
                <p className="login-subtitle">{t('auth:mfa.subtitle')}</p>

                {displayError && (
                  <div className="login-error">
                    <AlertCircle size={16} />
                    <span>{displayError}</span>
                  </div>
                )}

                <form onSubmit={handleMFAVerify}>
                  <div className="form-group">
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type="text"
                        placeholder={t('auth:mfa.codePlaceholder')}
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                  </div>
                  <button type="submit" className="login-button" disabled={isLoading || mfaCode.length < 6}>
                    {isLoading ? (
                      <span className="button-loading">
                        <Loader2 size={20} className="spinner" />
                        {t('auth:mfa.verifying')}
                      </span>
                    ) : (
                      t('auth:mfa.verify')
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Step 2: Credentials Form */}
            {selectedRole && currentRole && !mfaState.required && (
              <div className="login-credentials">
                <div className="login-credentials-topbar">
                  <button
                    type="button"
                    className="back-to-roles"
                    onClick={loginMethod === 'password' && authStep === 'password' ? handleBackToIdentifier : handleBackToRoles}
                  >
                    <ArrowLeft size={18} />
                    <span>{t('common:buttons.back')}</span>
                  </button>
                  <div className="selected-role-badge" style={{ background: currentRole.bg, color: currentRole.color }}>
                    {React.createElement(currentRole.icon, { size: 18 })}
                    <span>{currentRole.label}</span>
                  </div>
                </div>

                <h2 className="login-title">
                  {loginMethod === 'password' && authStep === 'password'
                    ? t('auth:steps.passwordTitle')
                    : loginMethod === 'password'
                      ? t('auth:steps.identifierTitle')
                      : t('auth:signIn')}
                </h2>
                <p className="login-subtitle">
                  {loginMethod === 'password' && authStep === 'password'
                    ? t('auth:signInSubtitle')
                    : loginMethod === 'password'
                      ? selectedRole === 'employee'
                        ? t('auth:steps.usernameSubtitle')
                        : t('auth:steps.emailSubtitle')
                      : t('auth:signInSubtitle')}
                </p>

                {loginMethod === 'password' && (
                  <div className="login-step-indicator" aria-hidden="true">
                    <span className={`login-step-dot ${authStep === 'identifier' ? 'active' : ''}`} />
                    <span className={`login-step-dot ${authStep === 'password' ? 'active' : ''}`} />
                  </div>
                )}

                {/* Login Method Tabs - Employee and Corporate HR can pick email/password or OTP */}
                {(selectedRole === 'corporate_hr' || selectedRole === 'employee') && (
                  <div className="login-method-tabs">
                    <button
                      className={`method-tab ${loginMethod === 'password' ? 'active' : ''}`}
                      onClick={() => setLoginMethod('password')}
                      type="button"
                    >
                      <Mail size={16} />
                      <span>{t('auth:methods.emailPassword')}</span>
                    </button>
                    <button
                      className={`method-tab ${loginMethod === 'otp' ? 'active' : ''}`}
                      onClick={() => setLoginMethod('otp')}
                      type="button"
                    >
                      <Smartphone size={16} />
                      <span>{t('auth:methods.otpLogin')}</span>
                    </button>
                  </div>
                )}

                {/* Error Display */}
                {displayError && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{displayError}</span>
                  </div>
                )}
                {!displayError && loginNotice && (
                  <div className="login-error login-notice">
                    <AlertCircle size={18} />
                    <span>{loginNotice}</span>
                  </div>
                )}

                {/* Success Message for OTP */}
                {otpMessage && (
                  <div className="login-success">
                    <CheckCircle2 size={18} />
                    <span>{otpMessage}</span>
                  </div>
                )}

                {/* Password Login — Step 1: identifier only */}
                {loginMethod === 'password' && authStep === 'identifier' && (
                  <form onSubmit={handleIdentifierContinue} className="login-form">
                    {selectedRole === 'employee' ? (
                      <div className="form-group">
                        <label htmlFor="username">{t('auth:form.username')}</label>
                        <div className="input-wrapper">
                          <UserCheck size={18} className="input-icon" />
                          <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toLowerCase())}
                            placeholder={t('auth:form.usernamePlaceholder')}
                            autoComplete="username"
                            autoFocus
                            required
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label htmlFor="email">{t('auth:form.emailAddress')}</label>
                        <div className="input-wrapper">
                          <Mail size={18} className="input-icon" />
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('auth:form.emailPlaceholder')}
                            autoComplete="email"
                            autoFocus
                            required
                          />
                        </div>
                      </div>
                    )}

                    <button type="submit" className="login-button">
                      {t('auth:steps.continue')}
                    </button>
                  </form>
                )}

                {/* Password Login — Step 2: password + sign in */}
                {loginMethod === 'password' && authStep === 'password' && (
                  <form onSubmit={handlePasswordLogin} className="login-form">
                    <div className="login-identifier-block">
                      <span className="login-identifier-label">{t('auth:steps.passwordSubtitle')}</span>
                      <div className="login-identifier-chip">
                        <span className="login-identifier-icon">
                          {selectedRole === 'employee' ? <UserCheck size={16} /> : <Mail size={16} />}
                        </span>
                        <span className="login-identifier-value">
                          {selectedRole === 'employee' ? username : email}
                        </span>
                        <button type="button" className="login-identifier-change" onClick={handleBackToIdentifier}>
                          {t('auth:steps.change')}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">{t('auth:form.password')}</label>
                      <div className="input-wrapper">
                        <Lock size={18} className="input-icon" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t('auth:form.passwordPlaceholder')}
                          autoComplete="current-password"
                          autoFocus
                          required
                        />
                        <button
                          type="button"
                          className="toggle-password"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Toggle password visibility"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="form-options">
                      <Link to="/activate-account" className="forgot-password-link" style={{ marginRight: 'auto' }}>
                        {t('auth:form.activateAccount')}
                      </Link>
                      <Link to="/forgot-password" className="forgot-password-link">
                        {t('auth:form.forgotPassword')}
                      </Link>
                    </div>

                    <button type="submit" className="login-button" disabled={isLoading}>
                      {isLoading ? (
                        <span className="button-loading">
                          <Loader2 size={20} className="spinner" />
                          {t('auth:loading.signingIn')}
                        </span>
                      ) : (
                        t('auth:signIn')
                      )}
                    </button>
                  </form>
                )}

                {/* OTP Login Form */}
                {loginMethod === 'otp' && (
                  <form onSubmit={handleOtpLogin} className="login-form">
                    <div className="form-group">
                      <label htmlFor="identifier">{t('auth:form.emailOrMobile')}</label>
                      <div className="otp-input-group">
                        <div className="input-wrapper" style={{ flex: 1 }}>
                          <Smartphone size={18} className="input-icon" />
                          <input
                            type="text"
                            id="identifier"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder={t('auth:form.emailOrMobilePlaceholder')}
                            autoComplete="email tel"
                            disabled={otpSent}
                            required
                          />
                        </div>
                        {!otpSent && (
                          <button
                            type="button"
                            className="send-otp-button"
                            onClick={handleRequestOtp}
                            disabled={otpSending || isLoading || !identifier}
                            aria-label={otpSending ? t('auth:otp.sendingOtp', 'Sending') : t('auth:otp.sendOtp')}
                          >
                            <span style={{ visibility: otpSending ? 'hidden' : 'visible' }}>
                              {t('auth:otp.sendOtp')}
                            </span>
                            {otpSending && (
                              <Loader2 size={16} className="spinner send-otp-spinner" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {otpSent && (
                      <>
                        <div className="form-group">
                          <label htmlFor="otp">{t('auth:otp.enterOtp')}</label>
                          <div className="input-wrapper">
                            <Lock size={18} className="input-icon" />
                            <input
                              type="text"
                              id="otp"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder={t('auth:otp.otpPlaceholder')}
                              maxLength={6}
                              autoComplete="one-time-code"
                              required
                            />
                          </div>
                        </div>

                        <div className="otp-actions">
                          <button
                            type="button"
                            className="otp-link"
                            onClick={handleRequestOtp}
                            disabled={otpSending || isLoading}
                            aria-label={otpSending ? t('auth:otp.sendingOtp', 'Sending') : t('auth:otp.resendOtp')}
                          >
                            {otpSending ? (
                              <Loader2 size={14} className="spinner" />
                            ) : (
                              t('auth:otp.resendOtp')
                            )}
                          </button>
                          <button
                            type="button"
                            className="otp-link"
                            onClick={() => {
                              setOtpSent(false);
                              setOtp('');
                              setOtpMessage('');
                            }}
                          >
                            {t('auth:otp.changeNumber')}
                          </button>
                        </div>

                        <button type="submit" className="login-button" disabled={isLoading}>
                          {isLoading ? (
                            <span className="button-loading">
                              <Loader2 size={20} className="spinner" />
                              {t('auth:loading.verifying')}
                            </span>
                          ) : (
                            t('auth:otp.verifyAndSignIn')
                          )}
                        </button>
                      </>
                    )}
                  </form>
                )}


                {/* Register Link */}
                <div className="login-footer">
                  <p>
                    {t('auth:register.dontHaveAccount')}{' '}
                    <Link to="/register/corporate" className="register-link">
                      {t('auth:corporateRegister.registerWithCode')}
                    </Link>
                  </p>
                  <p>
                    {t('auth:choosePlan.newHere')}{' '}
                    <Link to="/choose-plan" className="register-link">
                      {t('auth:choosePlan.seePlans')}
                    </Link>
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
